/**
 * Terrain Destruction — cratering, clearing, filling, collision resolution.
 *
 * All terrain modification functions that change cell values live here.
 */

import { TERRAIN, CELL } from '../constants';
import { random } from './rng';
import type { CraterShapeConfig } from '../types';
import { detectAndCollapseFragments } from './structural';
import {
  getGrid, isPointInTerrain,
  rebuildColumnTopCacheRange,
  worldToGridX, worldToGridY,
} from './grid-core';
import { applyScorch } from './grid-serialization';

const { CELL_SIZE } = TERRAIN;

// ─── Types ──────────────────────────────────────────────────────────────────────

export type GridBounds = { minGx: number; maxGx: number; minGy: number; maxGy: number };

export interface CollapseResult {
  cells: number;                              // total wireframe cells collapsed
  bounds: GridBounds;                         // bounding box of all collapsed cells
  centroids: { x: number; y: number }[];      // world-space centroids per collapsed component
}

export interface CraterResult {
  finalBounds: GridBounds;
  collapse: CollapseResult | null;
}

// ─── Noise helper ───────────────────────────────────────────────────────────────

// Radial noise for organic crater shapes (phase-shifted sine waves)
function craterNoise(angle: number, freq: number, seed: number): number {
  const n1 = Math.sin(angle * freq + seed) * 0.5;
  const n2 = Math.sin(angle * freq * 2.3 + seed + 1.7) * 0.3;
  const n3 = Math.sin(angle * freq * 5.1 + seed + 3.2) * 0.2;
  return n1 + n2 + n3;
}

/** Shared noisy circle iteration — generates seeds, computes bounding box,
 *  iterates cells calling cellOp for each cell inside the noisy radius.
 *  verticalBias shifts the effective center downward (used by crater clearing). */
function applyNoisyCircle(
  wcx: number, wcy: number, wRadius: number,
  config: CraterShapeConfig, verticalBias: number,
  cellOp: (gx: number, gy: number, dist: number, noiseR: number) => void,
): GridBounds {
  const seed1 = random() * Math.PI * 20;
  const seed2 = random() * Math.PI * 20;
  const seed3 = random() * Math.PI * 20;

  const biasedWcy = wcy + verticalBias * wRadius;
  const gcx = wcx / CELL_SIZE;
  const gcy = biasedWcy / CELL_SIZE;
  const gRadius = wRadius / CELL_SIZE;
  const maxAmp = config.primaryAmp + config.detailAmp + config.fineAmp;
  const expandedGRadius = gRadius * (1 + maxAmp);

  const minGx = Math.max(0, Math.floor(gcx - expandedGRadius));
  const maxGx = Math.min(TERRAIN.GRID_WIDTH - 1, Math.ceil(gcx + expandedGRadius));
  const minGy = Math.max(0, Math.floor(gcy - expandedGRadius));
  const maxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, Math.ceil(gcy + expandedGRadius));

  for (let gy = minGy; gy <= maxGy; gy++) {
    const dy = gy - gcy;
    for (let gx = minGx; gx <= maxGx; gx++) {
      const dx = gx - gcx;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > expandedGRadius) continue;

      const angle = Math.atan2(dy, dx);
      const noiseR = gRadius * (1
        + craterNoise(angle, config.primaryFreq, seed1) * config.primaryAmp
        + craterNoise(angle, config.detailFreq, seed2) * config.detailAmp
        + craterNoise(angle, config.fineFreq, seed3) * config.fineAmp
      );

      if (dist < noiseR) {
        cellOp(gx, gy, dist, noiseR);
      }
    }
  }

  return { minGx, maxGx, minGy, maxGy };
}

// ─── Destruction functions ──────────────────────────────────────────────────────

function clearNoisyCrater(
  wcx: number, wcy: number, wRadius: number, config: CraterShapeConfig,
  canDestroyWireframe = false, wireframeScale = 1.0,
): GridBounds | null {
  const grid = getGrid();
  if (!grid) return null;

  return applyNoisyCircle(wcx, wcy, wRadius, config, config.verticalBias, (gx, gy, dist, noiseR) => {
    const idx = gy * TERRAIN.GRID_WIDTH + gx;
    if (grid[idx] === CELL.WIREFRAME) {
      if (canDestroyWireframe && dist < noiseR * wireframeScale) {
        grid[idx] = 0;
      }
    } else if (grid[idx] === CELL.SAND) {
      grid[idx] = 0;
    }
  });
}

/** Clear a perfect circle of terrain (no noise). Used by Drill weapon for carving. */
export function clearCircle(
  wcx: number, wcy: number, wRadius: number,
  canDestroyWireframe = false, wireframeScale = 1.0,
): GridBounds | null {
  const grid = getGrid();
  if (!grid) return null;

  // Round to prevent sub-pixel float divergence between multiplayer clients
  wcx = Math.round(wcx);
  wcy = Math.round(wcy);

  const gcx = wcx / CELL_SIZE;
  const gcy = wcy / CELL_SIZE;
  const gRadius = wRadius / CELL_SIZE;
  const gRadiusSq = gRadius * gRadius;

  const minGx = Math.max(0, Math.floor(gcx - gRadius));
  const maxGx = Math.min(TERRAIN.GRID_WIDTH - 1, Math.ceil(gcx + gRadius));
  const minGy = Math.max(0, Math.floor(gcy - gRadius));
  const maxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, Math.ceil(gcy + gRadius));

  const wireframeSq = gRadiusSq * wireframeScale * wireframeScale;

  for (let gy = minGy; gy <= maxGy; gy++) {
    const dy = gy - gcy;
    for (let gx = minGx; gx <= maxGx; gx++) {
      const dx = gx - gcx;
      const distSq = dx * dx + dy * dy;
      if (distSq <= gRadiusSq) {
        const idx = gy * TERRAIN.GRID_WIDTH + gx;
        if (grid[idx] === CELL.WIREFRAME) {
          if (canDestroyWireframe && distSq <= wireframeSq) {
            grid[idx] = 0;
          }
        } else if (grid[idx] !== CELL.EMPTY) {
          grid[idx] = 0;
        }
      }
    }
  }

  rebuildColumnTopCacheRange(minGx, maxGx);

  return { minGx, maxGx, minGy, maxGy };
}

/** Fill a noisy circle — mirror of clearNoisyCrater, sets cells to SAND (terrain creation). */
export function fillNoisyCircle(
  wcx: number, wcy: number, wRadius: number, config: CraterShapeConfig,
): GridBounds | null {
  const grid = getGrid();
  if (!grid) return null;

  const bounds = applyNoisyCircle(wcx, wcy, wRadius, config, 0, (gx, gy) => {
    grid[gy * TERRAIN.GRID_WIDTH + gx] = 1;
  });

  rebuildColumnTopCacheRange(bounds.minGx, bounds.maxGx);

  return bounds;
}

/** Fill a vertical wall — rectangular region extending UPWARD from impact point.
 *  Sinusoidal noise on left/right edges for organic feel.
 *  Used by Mason weapons for terrain creation. */
export function fillVerticalWall(
  wcx: number, wcy: number, height: number, width: number,
): GridBounds | null {
  const grid = getGrid();
  if (!grid) return null;

  const halfW = width / 2;
  const seed = random() * Math.PI * 20;

  // Wall extends upward from wcy
  const minWx = wcx - halfW;
  const maxWx = wcx + halfW;
  const minWy = wcy - height;
  const maxWy = wcy;

  const minGx = Math.max(0, Math.floor(minWx / CELL_SIZE));
  const maxGx = Math.min(TERRAIN.GRID_WIDTH - 1, Math.ceil(maxWx / CELL_SIZE));
  const minGy = Math.max(0, Math.floor(minWy / CELL_SIZE));
  const maxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, Math.ceil(maxWy / CELL_SIZE));

  const gcx = wcx / CELL_SIZE;
  const noiseAmp = (width / CELL_SIZE) * 0.15; // 15% of width

  for (let gy = minGy; gy <= maxGy; gy++) {
    // Vertical position normalized 0-1 (bottom to top)
    const t = (maxGy - gy) / Math.max(1, maxGy - minGy);
    // Sinusoidal edge displacement — varies along height
    const edgeNoise = Math.sin(t * Math.PI * 3 + seed) * noiseAmp
      + Math.sin(t * Math.PI * 7 + seed + 2.1) * noiseAmp * 0.4;

    for (let gx = minGx; gx <= maxGx; gx++) {
      const dx = gx - gcx;
      const halfGw = (width / CELL_SIZE) * 0.5;
      // Apply noise to edges — shrink/expand effective width
      if (Math.abs(dx) <= halfGw + edgeNoise) {
        grid[gy * TERRAIN.GRID_WIDTH + gx] = 1;
      }
    }
  }

  rebuildColumnTopCacheRange(minGx, maxGx);

  return { minGx, maxGx, minGy, maxGy };
}

/** Clear all terrain from sky (gy=0) down to impact point across a narrow column.
 *  Mirror of clearFullDepthFissure (which goes impact → bottom).
 *  Used by Orbital weapons — beam from above carves a vertical channel. */
export function clearVerticalBeam(
  wcx: number, wcy: number, width: number,
): GridBounds | null {
  const grid = getGrid();
  if (!grid) return null;

  // Round to prevent sub-pixel float divergence between multiplayer clients
  wcx = Math.round(wcx);
  wcy = Math.round(wcy);

  const halfW = width / 2;
  const minGx = Math.max(0, Math.floor((wcx - halfW) / CELL_SIZE));
  const maxGx = Math.min(TERRAIN.GRID_WIDTH - 1, Math.ceil((wcx + halfW) / CELL_SIZE));
  const minGy = 0;
  const maxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, Math.floor(wcy / CELL_SIZE));

  for (let gy = minGy; gy <= maxGy; gy++) {
    for (let gx = minGx; gx <= maxGx; gx++) {
      grid[gy * TERRAIN.GRID_WIDTH + gx] = 0;
    }
  }

  rebuildColumnTopCacheRange(minGx, maxGx);

  return { minGx, maxGx, minGy, maxGy };
}

// ─── Thin debris removal ────────────────────────────────────────────────────────

/**
 * Remove sand cells that are part of horizontal runs thinner than minThickness.
 * Eliminates thin debris lines left after craters. Checks each row independently.
 * Returns true if any cells were removed.
 */
export function removeThinSand(
  minGx: number, maxGx: number, minGy: number, maxGy: number,
  minThickness: number,
): boolean {
  const grid = getGrid();
  if (!grid) return false;

  const startGx = Math.max(0, minGx);
  const endGx = Math.min(TERRAIN.GRID_WIDTH - 1, maxGx);
  const startGy = Math.max(0, minGy);
  const endGy = Math.min(TERRAIN.GRID_HEIGHT - 1, maxGy);
  let changed = false;

  for (let gy = startGy; gy <= endGy; gy++) {
    // Scan for horizontal runs of solid cells. When a run ends,
    // check if it was all sand (no wireframe) and too thin.
    let runStart = -1;
    let runHasWireframe = false;

    for (let gx = startGx; gx <= endGx + 1; gx++) {
      const idx = gy * TERRAIN.GRID_WIDTH + gx;
      const isSolid = gx <= endGx && grid[idx] > 0;

      if (isSolid) {
        if (runStart < 0) {
          runStart = gx;
          runHasWireframe = false;
        }
        if (grid[idx] === CELL.WIREFRAME) runHasWireframe = true;
      } else if (runStart >= 0) {
        // Run ended — check if it's a thin pure-sand run
        const runLen = gx - runStart;
        if (runLen < minThickness && !runHasWireframe) {
          for (let rx = runStart; rx < gx; rx++) {
            grid[gy * TERRAIN.GRID_WIDTH + rx] = CELL.EMPTY;
            changed = true;
          }
        }
        runStart = -1;
      }
    }
  }

  return changed;
}

// ─── Penetration resolution ─────────────────────────────────────────────────────

/** Penetration resolution — sample 8 perimeter probes around the tank circle.
 *  For each probe inside terrain, accumulate a push vector (probe → center direction).
 *  Returns push-out direction, depth, and whether any contact occurred.
 *  Concave corner safety: if contacts exist but push vector is near-zero
 *  (opposing forces cancel), default to pushing straight up. */
export function resolveTerrainPenetration(
  cx: number, cy: number, radius: number,
  terrainTest: (wx: number, wy: number) => boolean = isPointInTerrain,
): { pushX: number; pushY: number; nx: number; ny: number; depth: number; contact: boolean } {
  const PROBE_COUNT = 8;
  let sumPx = 0;
  let sumPy = 0;
  let contacts = 0;
  let maxDepth = 0;

  for (let i = 0; i < PROBE_COUNT; i++) {
    const angle = (i / PROBE_COUNT) * Math.PI * 2;
    const px = cx + Math.cos(angle) * radius;
    const py = cy + Math.sin(angle) * radius;

    if (terrainTest(px, py)) {
      // Push direction: from probe toward center
      const dx = cx - px;
      const dy = cy - py;
      sumPx += dx;
      sumPy += dy;
      contacts++;

      // Estimate depth: sample inward to find how far the probe is embedded
      const step = radius * 0.25;
      let depth = 1;
      for (let d = 1; d <= 3; d++) {
        const sx = px + (dx / radius) * step * d;
        const sy = py + (dy / radius) * step * d;
        if (terrainTest(sx, sy)) {
          depth = d + 1;
        } else {
          break;
        }
      }
      maxDepth = Math.max(maxDepth, depth * step);
    }
  }

  // Also check center point — deep embedding
  if (terrainTest(cx, cy)) {
    sumPy -= radius; // push up
    contacts++;
    maxDepth = Math.max(maxDepth, radius);
  }

  if (contacts === 0) {
    return { pushX: 0, pushY: 0, nx: 0, ny: 0, depth: 0, contact: false };
  }

  const len = Math.sqrt(sumPx * sumPx + sumPy * sumPy);

  // Concave corner safety: opposing probes cancel → near-zero push with contacts
  if (len < 0.1) {
    // Default: push straight up
    return {
      pushX: 0, pushY: -maxDepth,
      nx: 0, ny: -1,
      depth: maxDepth,
      contact: true,
    };
  }

  // Normalize push direction
  const nx = sumPx / len;
  const ny = sumPy / len;

  // Push distance scales with number of contacts and max probe depth
  // More contacts = deeper embedding = bigger push
  const pushDist = maxDepth * (contacts / PROBE_COUNT) + (contacts > 1 ? maxDepth * 0.5 : 0);
  const clampedPush = Math.min(pushDist, radius * 1.5); // don't teleport

  return {
    pushX: nx * clampedPush,
    pushY: ny * clampedPush,
    nx, ny,
    depth: maxDepth,
    contact: true,
  };
}

// ─── Crater pipeline ────────────────────────────────────────────────────────────

// removeDisconnectedTerrain — DISABLED
// Was: BFS flood from boundary-anchored cells, delete anything disconnected.
// Problem: A single crater near a ridge/narrow would sever huge terrain chunks,
// deleting thousands of cells of playable ground and ending games abruptly.
// Craters now only destroy terrain within the blast radius.

export function executeCraterPipeline(
  wcx: number, wcy: number, wRadius: number, config: CraterShapeConfig,
  canDestroyWireframe = false, wireframeScale = 1.0,
): CraterResult | null {
  const grid = getGrid();

  // 1. Destroy cells
  const bounds = clearNoisyCrater(wcx, wcy, wRadius, config, canDestroyWireframe, wireframeScale);
  if (!bounds) return null;

  // 2. Wall smoothing — DISABLED. Was shaving pre-existing terrain features (mesa
  // cliffs, pillars) that happened to be within the crater scan range. The game has
  // grapple hooks and generous traversal — crater walls don't need auto-smoothing.

  const finalBounds = { ...bounds };

  // 3. Structural collapse — detect small wireframe fragments and collapse them.
  // Old removeDisconnectedTerrain severed large chunks at ridges/narrows. New approach:
  // BFS connected components near crater, only collapse fragments ≤ FRAGMENT_THRESHOLD cells.
  const collapse = detectAndCollapseFragments(grid!, bounds);
  if (collapse) {
    finalBounds.minGx = Math.min(finalBounds.minGx, collapse.bounds.minGx);
    finalBounds.maxGx = Math.max(finalBounds.maxGx, collapse.bounds.maxGx);
    finalBounds.minGy = Math.min(finalBounds.minGy, collapse.bounds.minGy);
    finalBounds.maxGy = Math.max(finalBounds.maxGy, collapse.bounds.maxGy);
  }

  // 4. Remove thin sand debris at crater edges
  removeThinSand(finalBounds.minGx, finalBounds.maxGx, finalBounds.minGy, finalBounds.maxGy, 3);

  // 5. Update column cache
  rebuildColumnTopCacheRange(finalBounds.minGx, finalBounds.maxGx);

  // 6. Paint scorch (after smooth so new surfaces get scorched)
  applyScorch(wcx, wcy, wRadius);

  return { finalBounds, collapse };
}
